/**
 * DR Vitamin — the movable «تواصل» launcher (owner UX rules 15 and 16, 2026-09-12).
 *
 * 🔴 A TAP MUST NOT BE A DRAG, AND A DRAG MUST NOT OPEN THE SHEET. The whole control rests on that
 * distinction, so it is measured rather than guessed: the pointer has to travel MOVE_THRESHOLD before
 * anything moves, and once it has, the click that the browser fires afterwards is swallowed exactly
 * once. Without that swallow, every drag ends by opening the sheet.
 *
 * 🔴 IT SNAPS TO AN EDGE AND STAYS INSIDE THE VIEWPORT, including after a rotation. The stored
 * position is a side plus a vertical FRACTION, not a pixel pair, so a phone rotated from 812 to 375
 * high does not leave the button off-screen — which is what storing raw pixels does.
 *
 * 🔴 IT ALSO STEPS OVER THE THEME'S OWN FLOATING CONTROLS. back-to-top lives bottom-inline-start
 * at 50x50 and the right-to-left default put this button in the same 52px square, so the two drew on
 * top of each other. Their boxes are measured on every place() and this one rises to clear them.
 *
 * 🔴 AND IT GETS OUT OF THE WAY OF BUYING. The owner's rule is that it must not overlap Add to Cart
 * or Checkout. The cart page does not render it at all; here, whenever an add-to-cart control is
 * inside the bottom of the viewport, the launcher lifts by LIFT px until it is not.
 *
 * Position is remembered per device in localStorage, which can throw in a private window, so every
 * read and write is wrapped. Nothing here is sent anywhere.
 */
(function () {
  'use strict';
  var root = document.querySelector('[data-drv-cl]');
  if (!root) return;
  var btn = root.querySelector('[data-drv-cl-btn]');
  var panel = root.querySelector('[data-drv-cl-panel]');
  if (!btn || !panel) return;

  var KEY = 'drv.contact.launcher.pos';
  var MOVE_THRESHOLD = 8;   /* px of travel before a press becomes a drag */
  var EDGE = 12;            /* px from the screen edge when snapped */
  var LIFT = 72;            /* px to rise when a buy control is under it */
  var MIN_TOP = 64;         /* keep clear of the sticky header */
  var GAP = 10;             /* px of clearance kept from another floating control */
  /* The theme has its own floating controls. back-to-top sits bottom-inline-start at 50x50, which is
     exactly where a right-to-left default puts this button, so the two rendered on top of each other.
     They are treated as obstacles and measured rather than assumed, because their own position
     changes with scroll and template.
     Shopify's hCaptcha badge (bottom-right, fixed) appears once a contact form on the page is used — the Notify Me /
     Request availability panels — and sat on the English (right-side) button at 390px (measured 2026-09-14). It must stay
     visible, so the button steps over it like any other floating control. */
  var OBSTACLES = 'back-to-top, .back-to-top, .alo-to-top, [data-drv-contact], #shopify-chat, .shopify-chat, #shop-hcaptcha-badge-container';

  /* 🔴 PRODUCT PAGES REST IN THE BOTTOM CORNER AND NEVER COVER THE BUYING INFORMATION (owner,
     2026-09-14). At 82% of the viewport the button sat on the product title at first paint on real
     phones (375x812: button 666-710, title from 664; 360x740: button 607-651 over title 646-716).
     So on template-product only: rest PDP_BOTTOM px above the bottom edge plus the safe-area inset,
     and move to the NEAREST free slot whenever the title, price, quantity/variant controls, Add to
     Cart, payment buttons, gallery controls or the sticky purchase bar are under it. Nothing is a
     hardcoded height: the bands are measured on every scroll frame, the sticky bar included (it is
     display:none below 640px and slides in from the bottom above that). A saved drag position is
     still honoured as the preferred spot, but it is resolved through the same free-slot search, so it
     cannot park the button over those controls. Every other template keeps the 82% behaviour. */
  var PDP = document.body.classList.contains('template-product');
  /* 2026-09-15: search result rows carry their own Add to Cart, and collection pages carry filter/sort controls, so these
     templates use the same free-slot placement as the product page (owner: no contact overlay over buy or filter controls). */
  var LISTPAGE = document.body.classList.contains('template-search') || document.body.classList.contains('template-collection');
  var SLOT = PDP || LISTPAGE;
  /* 2026-10-04, owner-approved scoped exception to this file's freeze: `shop-wishlist` was missing, so the
     button parked on a product card's wishlist control on collection and search pages — measured with the
     button's own pointer-events disabled, where elementFromPoint returned shop-wishlist. Protection only; no
     placement, drag or sizing rule is touched. */
  var LIST_CRITICAL = '.drv-sr__add, .drv-sr__title, .drv-sr__price, .add-to-cart, [name="add"], .facets__summary, .facets-vertical summary, .mobile-facets__open, .facet-filters__field, .facet-filters, .collection-filters__item, .drv-notify > summary, .drv-demand > summary, shop-wishlist';
  var LIST_BUY = '.drv-sr__add, .add-to-cart, [name="add"]';
  var PDP_BOTTOM = 16;
  var PDP_GAP = 4;           /* clearance from a protected control; 10 px left no slot below the title on a 360x780 phone */
  /* 2026-10-04, same owner-approved scoped exception: `.drv-fullres` is the full-size image row added for
     P0-A, and `shop-wishlist` exists on the product page too. Without them the button sat on the
     "view full size" link (measured: 1 of 22 scroll positions, mobile Arabic, blocked by the button's svg).
     Protection only. */
  var PDP_CRITICAL = '#product-single h1, h1.product-name, .product-price, quantity-input.quantity, variant-radios, variant-selects, .product-form__input, .add-to-cart, [name="add"], .shopify-payment-button, .gallery-main .swiper-pagination, .gallery-main .swiper-button-next, .gallery-main .swiper-button-prev, .gallery-thumbnail, .sticky_atc_wrapper, .drv-alt__card, .drv-notify > summary, .drv-demand > summary, .drv-fullres, shop-wishlist';
  /* 2026-09-15: on an unavailable product the alternative card and the Notify Me / Request availability buttons are the
     buying controls (full width since the CTA redesign), so the button steps over them as it does over Add to Cart. */
  var PDP_BUY = '.add-to-cart, [name="add"], .shopify-payment-button, .sticky_atc_wrapper, .drv-notify > summary, .drv-demand > summary';

  var state = { side: null, frac: null }, hasSaved = false;
  try {
    var saved = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (saved && (saved.side === 'left' || saved.side === 'right') && typeof saved.frac === 'number') { state = saved; hasSaved = true; }
  } catch (e) { /* private window, or storage blocked: fall back to the default corner */ }

  var rtl = (document.documentElement.getAttribute('dir') || getComputedStyle(document.body).direction) === 'rtl';
  if (!state.side) state.side = rtl ? 'left' : 'right';
  if (typeof state.frac !== 'number') state.frac = 0.82;

  var insetCache = null;
  function safeInset(name, fallback) {
    if (insetCache !== null) return insetCache;
    var probe = document.createElement('div');
    probe.style.cssText = 'position:fixed;visibility:hidden;height:env(' + name + ',' + fallback + 'px)';
    document.body.appendChild(probe);
    var v = probe.getBoundingClientRect().height || fallback;
    probe.remove();
    insetCache = v;
    return v;
  }

  /* How far this box would have to rise to stop overlapping the theme's own floating controls.
     Returns 0 when it is already clear. Only visible, on-screen obstacles count, and a full-screen
     overlay (the mobile menu is 350x812) is not an obstacle to step around. */
  function obstacleLift(left, top, w, h) {
    var nodes = document.querySelectorAll(OBSTACLES);
    var rise = 0;
    for (var i = 0; i < nodes.length; i += 1) {
      var o = nodes[i];
      if (root.contains(o) || o.contains(root)) continue;
      var cs = getComputedStyle(o);
      if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') continue;
      var b = o.getBoundingClientRect();
      if (b.width < 8 || b.height < 8) continue;
      if (b.height > window.innerHeight * 0.6) continue;
      if (b.bottom < 0 || b.top > window.innerHeight) continue;
      var overlapX = Math.min(left + w, b.right) - Math.max(left, b.left);
      var overlapY = Math.min(top + h, b.bottom) - Math.max(top, b.top);
      if (overlapX > 0 && overlapY > 0) rise = Math.max(rise, (top + h) - b.top + GAP);
    }
    return rise;
  }

  /* Vertical bands [top, bottom] (with GAP) of visible elements in the viewport that share the
     button's horizontal span. */
  function bands(sel, left, w) {
    var out = [], nodes = document.querySelectorAll(sel), vh = window.innerHeight;
    for (var i = 0; i < nodes.length; i += 1) {
      var o = nodes[i];
      if (root.contains(o) || o.contains(root)) continue;
      var b = o.getBoundingClientRect();
      if (b.width < 2 || b.height < 2 || b.bottom <= 0 || b.top >= vh) continue;
      if (b.right <= left - PDP_GAP || b.left >= left + w + PDP_GAP) continue;
      var cs = getComputedStyle(o);
      if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') continue;
      out.push([b.top - PDP_GAP, b.bottom + PDP_GAP]);
    }
    return out;
  }
  /* The free top nearest to `natural` within [MIN_TOP, maxTop]. First every protected control; if no
     slot is free of all of them (a very short screen), only the purchase controls, so buying always
     stays tappable; failing both, the preferred spot. */
  function pdpTop(natural, left, w, h, maxTop) {
    var sets = PDP ? [PDP_CRITICAL + ', ' + OBSTACLES, PDP_BUY + ', ' + OBSTACLES] : [LIST_CRITICAL + ', ' + OBSTACLES, LIST_BUY + ', ' + OBSTACLES];
    for (var s = 0; s < sets.length; s += 1) {
      var iv = bands(sets[s], left, w), cands = [natural, maxTop], best = null;
      for (var i = 0; i < iv.length; i += 1) cands.push(iv[i][0] - h, iv[i][1]);
      for (var c = 0; c < cands.length; c += 1) {
        var t = cands[c];
        if (t < MIN_TOP || t > maxTop) continue;
        var free = true;
        for (var k = 0; k < iv.length && free; k += 1) if (t < iv[k][1] && t + h > iv[k][0]) free = false;
        if (free && (best === null || Math.abs(t - natural) < Math.abs(best - natural))) best = t;
      }
      if (best !== null) return best;
    }
    return natural;
  }

  var lifted = false, appliedRise = 0;
  function place() {
    var r = root.getBoundingClientRect();
    var w = r.width || 56, h = r.height || 56;
    var bottomInset = safeInset('safe-area-inset-bottom', 0);
    if (SLOT) {
      var pdpMax = Math.max(MIN_TOP, window.innerHeight - h - PDP_BOTTOM - bottomInset);
      var natural = hasSaved ? Math.min(Math.max(state.frac * window.innerHeight, MIN_TOP), pdpMax) : pdpMax;
      var pLeft = state.side === 'left' ? EDGE : window.innerWidth - w - EDGE;
      var next = Math.round(pdpTop(natural, pLeft, w, h, pdpMax));
      var cur = parseFloat(root.style.top);
      if (cur !== next) {
        /* A small step follows the page as it scrolls; a jump between slots is eased. */
        root.style.transition = (drag || isNaN(cur) || Math.abs(next - cur) < 24) ? 'none' : 'top 160ms ease-out';
        root.style.top = next + 'px';
      }
      if (state.side === 'left') { root.style.left = EDGE + 'px'; root.style.right = 'auto'; }
      else { root.style.right = EDGE + 'px'; root.style.left = 'auto'; }
      root.setAttribute('data-side', state.side);
      return;
    }
    var maxTop = window.innerHeight - h - EDGE - bottomInset - (lifted ? LIFT : 0);
    var top = Math.min(Math.max(state.frac * window.innerHeight, MIN_TOP), Math.max(MIN_TOP, maxTop));
    var left = state.side === 'left' ? EDGE : window.innerWidth - w - EDGE;
    /* Step over the theme's own floating controls, never under MIN_TOP. One pass is enough: the
       obstacles do not move in response to this, so a second pass cannot find a new overlap. */
    var rise = obstacleLift(left, top, w, h);
    appliedRise = rise;
    if (rise > 0) top = Math.max(MIN_TOP, top - rise);
    root.style.top = Math.round(top) + 'px';
    if (state.side === 'left') { root.style.left = EDGE + 'px'; root.style.right = 'auto'; }
    else { root.style.right = EDGE + 'px'; root.style.left = 'auto'; }
    root.setAttribute('data-side', state.side);
  }

  function persist() {
    hasSaved = true;   /* a drag on a product page is the preferred spot from now on, not only after a reload */
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch (e) { /* nothing to do */ }
  }

  /* ---- open / close ---- */
  function isOpen() { return btn.getAttribute('aria-expanded') === 'true'; }
  function open() {
    panel.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    /* Decide the direction from the room actually available, measured after the panel is laid out.
       Opening up is preferred only when down does not fit and up does; otherwise down stays, because
       a sheet clipped at the top is no better than one clipped at the bottom. */
    root.removeAttribute('data-open-up');
    var br = btn.getBoundingClientRect();
    var ph = panel.getBoundingClientRect().height;
    var fitsDown = br.bottom + 8 + ph <= window.innerHeight - 4;
    var fitsUp = br.top - 8 - ph >= 4;
    if (!fitsDown && fitsUp) root.setAttribute('data-open-up', '');
    var first = panel.querySelector('a');
    if (first) first.focus({ preventScroll: true });
    document.addEventListener('click', onOutside, true);
    document.addEventListener('keydown', onKey, true);
  }
  function close(focusBack) {
    panel.hidden = true;
    root.removeAttribute('data-open-up');
    btn.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', onOutside, true);
    document.removeEventListener('keydown', onKey, true);
    if (focusBack) btn.focus({ preventScroll: true });
  }
  function onOutside(e) { if (!root.contains(e.target)) close(false); }
  function onKey(e) { if (e.key === 'Escape') { e.stopPropagation(); close(true); } }

  /* ---- drag ---- */
  var drag = null, swallowClick = false;
  btn.addEventListener('pointerdown', function (e) {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    var r = root.getBoundingClientRect();
    drag = { id: e.pointerId, x0: e.clientX, y0: e.clientY, dy: e.clientY - r.top, moved: false };
    btn.setPointerCapture(e.pointerId);
  });
  btn.addEventListener('pointermove', function (e) {
    if (!drag || e.pointerId !== drag.id) return;
    var dx = e.clientX - drag.x0, dy = e.clientY - drag.y0;
    if (!drag.moved && Math.sqrt(dx * dx + dy * dy) < MOVE_THRESHOLD) return;
    if (!drag.moved) { drag.moved = true; root.setAttribute('data-dragging', ''); root.style.transition = 'none'; if (isOpen()) close(false); }
    e.preventDefault();
    var h = root.getBoundingClientRect().height || 56;
    var top = Math.min(Math.max(e.clientY - drag.dy, MIN_TOP), window.innerHeight - h - EDGE);
    root.style.top = Math.round(top) + 'px';
    /* Follow the finger horizontally while dragging; it snaps on release. */
    /* The button is now a labelled pill, not a 56px circle, so the clamp uses its real width —
       with the old constant a pill could be dragged partly off the right edge. */
    var bw = root.getBoundingClientRect().width || 56;
    root.style.left = Math.round(Math.min(Math.max(e.clientX - bw / 2, EDGE), window.innerWidth - bw - EDGE)) + 'px';
    root.style.right = 'auto';
  });
  function endDrag(e) {
    if (!drag || (e && e.pointerId !== drag.id)) return;
    var moved = drag.moved;
    drag = null;
    root.removeAttribute('data-dragging');
    if (!moved) return;                 /* a tap: let the click through to the button */
    swallowClick = true;                /* a drag: eat the click the browser is about to fire */
    var r = root.getBoundingClientRect();
    state.side = (r.left + r.width / 2) < window.innerWidth / 2 ? 'left' : 'right';
    state.frac = Math.min(Math.max(r.top / window.innerHeight, 0), 1);
    persist();
    place();
  }
  btn.addEventListener('pointerup', endDrag);
  btn.addEventListener('pointercancel', endDrag);

  btn.addEventListener('click', function (e) {
    if (swallowClick) { swallowClick = false; e.preventDefault(); e.stopPropagation(); return; }
    if (isOpen()) close(true); else open();
  });

  /* ---- stay clear of the buying controls ---- */
  var BUY = '.add-to-cart, [data-js-product-button-sold-out], button[name="checkout"], .cart__checkout, [name="add"]';
  var pending = false;
  function checkOverlap() {
    pending = false;
    if (SLOT) { if (!drag) place(); return; }
    var near = false;
    var nodes = document.querySelectorAll(BUY);
    for (var i = 0; i < nodes.length; i += 1) {
      var b = nodes[i].getBoundingClientRect();
      if (b.height === 0) continue;
      if (b.bottom > window.innerHeight - (LIFT + 96) && b.top < window.innerHeight) { near = true; break; }
    }
    /* 🔴 back-to-top IS HIDDEN AT THE TOP OF THE PAGE AND APPEARS ON SCROLL, so checking obstacles
       only on resize left the two drawn on top of each other for the whole scroll. The rise the
       current scroll position calls for is recomputed here and compared with the rise place() last
       applied; they differ exactly when the obstacle appeared, moved or went away. */
    var r = root.getBoundingClientRect();
    var w = r.width || 56, h = r.height || 56;
    var maxTop = window.innerHeight - h - EDGE - safeInset('safe-area-inset-bottom', 0) - (near ? LIFT : 0);
    var naturalTop = Math.min(Math.max(state.frac * window.innerHeight, MIN_TOP), Math.max(MIN_TOP, maxTop));
    var left = state.side === 'left' ? EDGE : window.innerWidth - w - EDGE;
    var wantRise = obstacleLift(left, naturalTop, w, h);
    if (near !== lifted || wantRise !== appliedRise) { lifted = near; place(); }
  }
  function schedule() { if (!pending) { pending = true; requestAnimationFrame(checkOverlap); } }

  place();
  root.hidden = false;
  window.addEventListener('resize', function () { insetCache = null; place(); schedule(); });
  window.addEventListener('orientationchange', function () { insetCache = null; setTimeout(function () { insetCache = null; place(); schedule(); }, 120); });
  window.addEventListener('scroll', schedule, { passive: true });
  if (PDP) {
    /* The sticky purchase bar slides in on a 250 ms transform after the scroll that triggered it, so
       the scroll frame still sees it below the viewport. Re-place when the theme toggles its body
       class, when any transition ends, and once more after the slide. */
    document.addEventListener('transitionend', schedule, true);
    if (window.MutationObserver) new MutationObserver(function () { schedule(); setTimeout(schedule, 320); }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }
  schedule();
}());
